import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ThemeName = 'theme-light' | 'theme-dark' | 'theme-nord' | 'theme-dracula' | 'theme-solarized' | 'theme-monokai' | 'theme-gruvbox' | 'theme-one-dark' | 'theme-catppuccin';

export interface ThemeOption {
  value: ThemeName;
  label: string;
  colors: { bg: string; accent: string; text: string };
  font: string;
}

const STORAGE_KEY = 'bhs-theme';
const FONT_STORAGE_KEY = 'bhs-font';

const LEGACY_MAP: Record<string, ThemeName> = {
  dark: 'theme-dark',
  light: 'theme-light',
};

@Injectable({ providedIn: 'root' })
export class ThemeService {
  static readonly THEMES: ThemeOption[] = [
    { value: 'theme-light',      label: 'Light',            colors: { bg: '#ffffff', accent: '#5dade2', text: '#181d1f' }, font: 'Arial' },
    { value: 'theme-dark',       label: 'Dark',             colors: { bg: '#1a2233', accent: '#5dade2', text: '#ecf0f1' }, font: 'Arial' },
    { value: 'theme-nord',       label: 'Nord',             colors: { bg: '#2e3440', accent: '#88c0d0', text: '#eceff4' }, font: 'Arial' },
    { value: 'theme-dracula',    label: 'Dracula',          colors: { bg: '#282a36', accent: '#bd93f9', text: '#f8f8f2' }, font: 'Arial' },
    { value: 'theme-solarized',  label: 'Solarized Dark',   colors: { bg: '#002b36', accent: '#268bd2', text: '#fdf6e3' }, font: 'Arial' },
    { value: 'theme-monokai',    label: 'Monokai',          colors: { bg: '#272822', accent: '#66d9ef', text: '#f8f8f2' }, font: 'Arial' },
    { value: 'theme-gruvbox',    label: 'Gruvbox',          colors: { bg: '#282828', accent: '#fabd2f', text: '#ebdbb2' }, font: 'Arial' },
    { value: 'theme-one-dark',   label: 'One Dark',         colors: { bg: '#282c34', accent: '#61afef', text: '#abb2bf' }, font: 'Arial' },
    { value: 'theme-catppuccin', label: 'Catppuccin Mocha', colors: { bg: '#1e1e2e', accent: '#cba6f7', text: '#cdd6f4' }, font: 'Arial' },
  ];

  static readonly FONT_OPTIONS: { value: string; label: string }[] = [
    // The only non-Google entry, and the only one that needs no network fetch:
    // it is installed everywhere, so it paints immediately instead of waiting
    // on the webfont. It is also what the status bar is pinned to, so choosing
    // it makes the grid and the footer match exactly.
    { value: 'Arial', label: 'Arial (system)' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Inter', label: 'Inter' },
    { value: '"Source Sans 3"', label: 'Source Sans 3' },
    { value: '"Fira Sans"', label: 'Fira Sans' },
    { value: '"IBM Plex Sans"', label: 'IBM Plex Sans' },
    { value: '"Nunito Sans"', label: 'Nunito Sans' },
    { value: 'Rubik', label: 'Rubik' },
    { value: 'Manrope', label: 'Manrope' },
    { value: '"DM Sans"', label: 'DM Sans' },
    { value: '"Open Sans"', label: 'Open Sans' },
    { value: 'Lato', label: 'Lato' },
    { value: '"Noto Sans"', label: 'Noto Sans' },
    { value: '"PT Sans"', label: 'PT Sans' },
    { value: '"Work Sans"', label: 'Work Sans' },
    { value: 'Barlow', label: 'Barlow' },
    { value: 'Mulish', label: 'Mulish' },
    { value: 'Karla', label: 'Karla' },
    { value: 'Outfit', label: 'Outfit' },
    { value: '"Plus Jakarta Sans"', label: 'Plus Jakarta Sans' },
    { value: 'Lexend', label: 'Lexend' },
    { value: '"Space Grotesk"', label: 'Space Grotesk' },
    { value: 'Urbanist', label: 'Urbanist' },
    { value: '"Albert Sans"', label: 'Albert Sans' },
    { value: '"Exo 2"', label: 'Exo 2' },
    { value: '"Red Hat Display"', label: 'Red Hat Display' },
  ];

  private _theme$ = new BehaviorSubject<ThemeName>('theme-light');
  readonly theme$ = this._theme$.asObservable();

  private _font$ = new BehaviorSubject<string>('Arial');
  readonly font$ = this._font$.asObservable();

  init(): void {
    const raw = localStorage.getItem(STORAGE_KEY) || 'light';
    const mapped = LEGACY_MAP[raw] || raw as ThemeName;
    const valid = ThemeService.THEMES.some(t => t.value === mapped) ? mapped : 'theme-light';
    this.applyTheme(valid);

    // Apply font override if saved
    const savedFont = localStorage.getItem(FONT_STORAGE_KEY);
    if (savedFont) {
      this.applyFont(savedFont);
    }
  }

  setTheme(theme: ThemeName): void {
    localStorage.setItem(STORAGE_KEY, theme);
    this.applyTheme(theme);
  }

  getTheme(): ThemeName {
    return this._theme$.value;
  }

  setFont(font: string): void {
    localStorage.setItem(FONT_STORAGE_KEY, font);
    this.applyFont(font);
  }

  getFont(): string {
    return this._font$.value;
  }

  resetFont(): void {
    localStorage.removeItem(FONT_STORAGE_KEY);
    const entry = ThemeService.THEMES.find(t => t.value === this._theme$.value);
    const themeFont = entry ? entry.font : 'Arial';
    this.applyFont(themeFont);
  }

  /** Returns the current theme's default font value */
  getThemeDefaultFont(): string {
    const entry = ThemeService.THEMES.find(t => t.value === this._theme$.value);
    return entry ? entry.font : 'Arial';
  }

  private applyFont(font: string): void {
    this._font$.next(font);
    document.body.style.setProperty('--bhs-font-family', `${font}, "Helvetica Neue", sans-serif`);
  }

  private applyTheme(theme: ThemeName): void {
    const body = document.body;
    // Remove all theme classes (including legacy)
    body.classList.remove('dark-theme', ...ThemeService.THEMES.map(t => t.value));
    body.classList.add(theme);
    this._theme$.next(theme);
    localStorage.setItem(STORAGE_KEY, theme);

    // Apply per-theme font (only if no user override)
    const entry = ThemeService.THEMES.find(t => t.value === theme);
    if (entry && !localStorage.getItem(FONT_STORAGE_KEY)) {
      this.applyFont(entry.font);
    }
  }
}
